/**
 * @packageDocumentation
 * Canonical resizing for Pixi canvases using fixed design spaces:
 * - Landscape: 1920x1080
 * - Portrait: 1080x1920
 * Background canvas (app_bg) is always 1920x1080 and CSS-scaled as "cover".
 * Other canvases are CSS-scaled as "contain".
 */

import type { Application } from "pixi.js";
import { BottomBar } from "./Game/bottomBar";
import {
  PortraitAdjustFun,
  landScapeAdjustFun,
  PortraitAdjust_preloader,
  landscapeAdjust_preloader,
} from "./app";

interface appDataType {
  app: Application;
  app_width: number; // kept for compatibility (ignored for canonical sizing)
  app_height: number; // kept for compatibility (ignored for canonical sizing)
  resize_to_device: number;
  transformOrigin_Y: number; // kept for compatibility (ignored)
  translate_Y: number; // used for non-bg canvases
  app_top: number; // used for non-bg canvases
  app_name: string; // "app" | "app_bg" | "bottom_app" | etc.
}

const DESIGN = {
  LANDSCAPE: { w: 1920, h: 1080 },
  PORTRAIT: { w: 1080, h: 1920 },
  BG: { w: 1920, h: 1080 }, // background texture is 1920x1080
};

function getViewport() {
  const vv = window.visualViewport;
  const w = vv ? vv.width / (vv.scale || 1) : window.innerWidth;
  const h = vv ? vv.height / (vv.scale || 1) : window.innerHeight;
  return { w, h };
}

function isPortrait(vw: number, vh: number) {
  return vh > vw;
}

function setCenteredCanvasStyles(
  canvas: HTMLCanvasElement,
  topPercent: number,
  translateYPercent: number,
  zIndex: string
) {
  canvas.style.position = "absolute";
  canvas.style.left = "50%";
  canvas.style.top = `${topPercent}%`;
  canvas.style.bottom = ""; // reset (in case previously pinned)
  canvas.style.transformOrigin = "50% 50%";
  canvas.style.willChange = "transform";
  canvas.style.zIndex = zIndex;
  canvas.style.pointerEvents = "auto";
  canvas.style.transform = `translate(-50%, -${translateYPercent}%)`;
}

// ✅ bottom_app ONLY: hard-pin to bottom and keep X centered
function setBottomPinnedCanvasStyles(canvas: HTMLCanvasElement, zIndex: string) {
  canvas.style.position = "absolute";
  canvas.style.left = "50%";
  canvas.style.top = "";       // do not use top for bottom bar
  canvas.style.bottom = "0px"; // pin to bottom of viewport/container
  canvas.style.transformOrigin = "50% 100%"; // bottom-center
  canvas.style.willChange = "transform";
  canvas.style.zIndex = zIndex;
  canvas.style.pointerEvents = "auto";
}


function applyScale(canvas: HTMLCanvasElement, scale: number) {
  const base = canvas.style.transform; // translate(...)
  canvas.style.transform = `${base} scale(${scale})`;
}

export function StartAppResize(appData: appDataType) {
  const { app, resize_to_device, translate_Y, app_top, app_name } = appData;
  document.body.style.backgroundImage = "url('./assets/Images/BG_veiledVillains_01.png')";

  function resizer() {
    const { w: vw, h: vh } = getViewport();
    const portrait = isPortrait(vw, vh);

    const isBg = app_name === "app_bg";
    const isBottom = app_name === "bottom_app";

    // Choose renderer design size (the internal coordinate system)
    const design = isBg
      ? DESIGN.BG
      : isBottom
        // ✅ Smaller bottom bar canvas, still pinned via app_top=100 / translate_Y=100 from bottomBar.ts
        ? (portrait ? { w: 1080, h: 140 } : { w: 1920, h: 90 })
        : (portrait ? DESIGN.PORTRAIT : DESIGN.LANDSCAPE);


    // Set the unscaled CSS box to the design size
    const canvas = app.canvas;
    canvas.style.width = `${design.w}px`;
    canvas.style.height = `${design.h}px`;

    // z-index stacking (bg behind everything)
    const zIndex = isBg ? "0" : isBottom ? "3" : "2";

    // Center everything with a consistent model.
    // BG should always be true center: top=50 translateY=50
    const topPercent = isBg ? 50 : app_top;
    const translateYPct = isBg ? 50 : translate_Y;

    // Compute CSS scale:
    // - BG = cover
    // - Others = contain
    const scaleX = vw / design.w;
    const scaleY = vh / design.h;
    const scale = isBg ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);

    // ✅ Styles + transform per-canvas type
    if (isBottom) {
      // Bottom bar: pin to bottom, center on X, no Y-translate math.
      setBottomPinnedCanvasStyles(canvas, zIndex);
      canvas.style.transform = `translateX(-50%) scale(${scale})`;
    } else {
      // Everything else: original centering model
      setCenteredCanvasStyles(canvas, topPercent, translateYPct, zIndex);
      applyScale(canvas, scale);
    }



    // Resize renderer to match the design size (critical for layout + pointer coords)
    app.renderer.resize(design.w, design.h);

    // Normalize stage; app.ts should position content using renderer.screen w/h
    app.stage.scale.set(1);
    app.stage.position.set(0, 0);

    // Call per-orientation hooks AFTER renderer resize
    if (!isBg) {
      if (portrait) {
        PortraitAdjust_preloader?.();
        PortraitAdjustFun?.();
      } else {
        landscapeAdjust_preloader?.();
        landScapeAdjustFun?.();
      }
    }

    // If bottom bar has its own layout functions, call them here so they use new renderer size
    if (isBottom) {
      if (portrait) BottomBar.AlignPortrait();
      else BottomBar.AlignLandscape();
    }
  }

  if (!resize_to_device) {
    // no scaling: just set a baseline landscape design space
    const isBg = app_name === "app_bg";
    const design = isBg ? DESIGN.BG : DESIGN.LANDSCAPE;

    app.renderer.resize(design.w, design.h);

    const canvas = app.canvas;
    canvas.style.width = `${design.w}px`;
    canvas.style.height = `${design.h}px`;

    const zIndex = isBg ? "0" : app_name === "bottom_app" ? "3" : "2";
    const topPercent = isBg ? 50 : app_top;
    const translateYPct = isBg ? 50 : translate_Y;

    if (app_name === "bottom_app") {
      setBottomPinnedCanvasStyles(canvas, zIndex);
      canvas.style.transform = `translateX(-50%) scale(1)`;
    } else {
      setCenteredCanvasStyles(canvas, topPercent, translateYPct, zIndex);
      applyScale(canvas, 1);
    }


    return;
  }

  let raf = 0;
  const onResize = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(resizer);
  };

  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
  window.visualViewport?.addEventListener("resize", onResize);
  window.visualViewport?.addEventListener("scroll", onResize);

  resizer();
}
